// WebGL2 sorts the splats on the CPU and needs every chunk's centres. The engine gets them with a GPU pass and a
// synchronous readback (getBufferSubData) per loaded file: 21 % of main-thread time and every hitch over 100 ms in
// the 31.10.2026 measurements (docs/performance/bericht.md). The positions are already in the decoded WebP images,
// so this decodes them on the CPU with the engine's own formula (GSplatSogData read(): lerp, then sign·(e^|v| - 1)).
// Measured on keller-p0: 8.1 s of blocked main thread per 90 s became 1.3 s of decoding, frames over 50 ms 17 -> 1.
// The decoding runs in a Web Worker: on the main thread the three largest Wohnung files took 20-36 ms each (01.11.2026),
// over the 16 ms frame budget. The main thread only copies the two image handles (createImageBitmap) and hands them over.
// The first chunk also runs the GPU path and compares; any mismatch, an unknown image source or a failing worker keeps
// the GPU path.
import { GSplatSogData } from 'playcanvas';

const MAX_ERROR = 1e-3;   // metres between CPU and GPU centres on the first chunk (measured: under 2e-6)

let verdict = null;       // null until the first chunk was compared, then true (worker) or false (GPU path)
let testing = null;       // the first chunk's comparison while it runs; later chunks wait for its verdict
let worker = null, broken = false, nextId = 0;
const pending = new Map();

const isImage = (src) => (typeof ImageBitmap !== 'undefined' && src instanceof ImageBitmap)
    || (typeof HTMLImageElement !== 'undefined' && src instanceof HTMLImageElement);

// Runs inside the worker as its own script (stringified), so it must not use anything from this module
function centresWorker() {
    let canvas = null, ctx = null;
    const pixels = (bmp, w, h) => {
        if (!canvas || canvas.width !== w || canvas.height !== h) {
            canvas = new OffscreenCanvas(w, h);
            ctx = canvas.getContext('2d', { willReadFrequently: true });
        }
        ctx.clearRect(0, 0, w, h);
        ctx.drawImage(bmp, 0, 0);
        return ctx.getImageData(0, 0, w, h, { colorSpace: 'srgb' }).data;
    };
    self.onmessage = ({ data: { id, lo, hi, w, h, n, mins, maxs } }) => {
        const t0 = performance.now();
        try {
            if (w * h < n) throw new Error('images smaller than the splat count');
            const l = pixels(lo, w, h), u = pixels(hi, w, h);
            const out = new Float32Array(n * 3);
            for (let i = 0; i < n; i++) {
                const b = i * 4;
                for (let k = 0; k < 3; k++) {
                    const v = mins[k] + (maxs[k] - mins[k]) * (((u[b + k] << 8) + l[b + k]) / 65535);
                    out[i * 3 + k] = Math.sign(v) * (Math.exp(Math.abs(v)) - 1);
                }
            }
            self.postMessage({ id, centers: out, ms: performance.now() - t0 }, [out.buffer]);
        } catch (e) {
            self.postMessage({ id, centers: null, error: String(e) });
        } finally {
            lo.close();
            hi.close();
        }
    };
}

function fail(reason) {
    if (!broken) console.warn('Splat centre worker failed; keeping the GPU readback', reason);
    broken = true;
    for (const done of pending.values()) done(null);
    pending.clear();
}

function startWorker() {
    const w = new Worker(URL.createObjectURL(new Blob([`(${centresWorker})()`], { type: 'text/javascript' })));
    w.onmessage = ({ data }) => {
        const done = pending.get(data.id);
        pending.delete(data.id);
        if (!data.centers) fail(data.error);
        done?.(data.centers);
    };
    w.onerror = (e) => fail(e.message || 'worker script error');
    return w;
}

// Centres of one SOG chunk from its means_l/means_u images, decoded in the worker; null when the sources are not
// plain images or the worker cannot run
async function decode(data) {
    const lo = data.means_l?._levels?.[0], hi = data.means_u?._levels?.[0];
    if (broken || !isImage(lo) || !isImage(hi) || !data.meta?.means) return null;
    let l = null, u = null;
    try {
        // Copies of the image handles: the textures still need their own for the upload (the engine closes them after it)
        const opts = { premultiplyAlpha: 'none', colorSpaceConversion: 'none' };
        [l, u] = await Promise.all([createImageBitmap(lo, opts), createImageBitmap(hi, opts)]);
        worker ??= startWorker();
        if (broken) throw new Error('worker failed');
        const { mins, maxs } = data.meta.means;
        return await new Promise((resolve) => {
            const id = nextId++;
            pending.set(id, resolve);
            worker.postMessage({ id, lo: l, hi: u, w: data.means_l.width, h: data.means_l.height, n: data.numSplats, mins, maxs }, [l, u]);
        });
    } catch (e) {
        l?.close();
        u?.close();
        fail(e);
        return null;
    }
}

/** Replaces GSplatSogData.generateCenters for the whole page. Call once, on WebGL2 only (WebGPU sorts on the GPU). */
export function installCpuCenters() {
    const proto = GSplatSogData.prototype;
    if (proto.__cpuCenters) return;
    const gpu = proto.generateCenters;
    proto.__cpuCenters = true;
    proto.generateCenters = async function () {
        if (testing) await testing;
        if (verdict === false) return gpu.call(this);
        if (verdict === true) {
            const out = await decode(this);
            if (!out) return gpu.call(this);
            if (!this.destroyed) this._centers = out;
            return;
        }
        let release;
        testing = new Promise((r) => { release = r; });
        try {
            const [out] = await Promise.all([decode(this), gpu.call(this)]);
            if (this.destroyed) return;   // unloaded while loading: the next chunk compares
            const g = this._centers;
            let err = out && g && g.length === out.length ? 0 : Infinity;
            for (let i = 0; err <= MAX_ERROR && i < g.length; i++) err = Math.max(err, Math.abs(g[i] - out[i]));
            verdict = err <= MAX_ERROR;
            if (verdict) this._centers = out;
            else console.warn('CPU splat centres differ from the GPU path or could not be decoded; keeping the GPU readback', err);
        } finally {
            testing = null;
            release();
        }
    };
}
